import { Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpErrorResponse,
  HttpBackend,
  HttpClient
} from '@angular/common/http';
import { Observable, BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { environment } from '../../environments/environment';
import { AppConfig } from '../services/app.config';

@Injectable({
  providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);
  private http: HttpClient;

  constructor(
    private authService: AuthService,
    private router: Router,
    httpBackend: HttpBackend, // raw backend to avoid recursion
    private appConfig: AppConfig
  ) {
    // create a HttpClient that bypasses interceptors for refresh token call
    this.http = new HttpClient(httpBackend);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // get token from AuthService (which uses sessionStorage)
    // console.log("Intercepting request:", req.url);
    // console.log("Current access token:", this.authService.getToken());
    const token = this.authService.getToken();

    // Attach Authorization header if token present
    let authReq = req;
    if (token) {
      authReq = req.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    return next.handle(authReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (error && (error.status === 401 || error.status === 403)) {
          // handle unauthorized and try refreshing token
          return this.handle401Error(authReq, next);
        }
        return throwError(() => error);
      })
    );
  }

  private handle401Error(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // If not already refreshing, start refresh flow
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.refreshTokenSubject.next(null);

      const refreshToken = this.authService.getRefreshToken();
      if (!refreshToken) {
        // no refresh token — logout and redirect
        this.authService.loggedOut();
        this.router.navigate(['/login']);
        return throwError(() => new Error('No refresh token available'));
      }

      const body = {
        client_id: environment.client_id,
        client_secret: environment.client_secret,
        refresh_token: refreshToken,
        grant_type: 'refresh_token'
      };

      // Use raw http client to call token endpoint (bypass interceptors)
      return this.http.post<any>(this.appConfig.tokenUrl, body).pipe(
        switchMap((tokenResp: any) => {
          this.isRefreshing = false;

          // expected tokenResp to contain access_token and refresh_token
          const newAccess = tokenResp?.audit_log_access_token;
          const newRefresh = tokenResp?.audit_log_refresh_token;

          if (newAccess) {
            // save tokens to sessionStorage via AuthService
            this.authService.setToken(newAccess, newRefresh);

            // also set cookie if you still need cookies (optional)
            if (newAccess) {
              document.cookie = `audit_log_access_token=${newAccess};domain=.${environment.cookiesDomain};path=/`;
            }
            if (newRefresh) {
              document.cookie = `audit_log_refresh_token=${newRefresh};domain=.${environment.cookiesDomain};path=/`;
            }

            // publish new token so queued requests proceed
            this.refreshTokenSubject.next(newAccess);

            // retry the original request with new access token
            return next.handle(this.addAuthHeader(request, newAccess));
          }

          // if refresh response missing token: logout
          this.authService.loggedOut();
          this.router.navigate(['/login']);
          return throwError(() => new Error('Token refresh response missing access_token'));
        }),
        catchError(err => {
          alert("Session expired. Please log in again.");
          // refresh failed — logout & redirect
          this.isRefreshing = false;
          this.authService.loggedOut();
          this.router.navigate(['/login']);
          return throwError(() => err);
        })
      );
    }

    // If refresh in progress, queue requests until refresh completes
    return this.refreshTokenSubject.pipe(
      filter(token => token != null),
      take(1),
      switchMap((token) => next.handle(this.addAuthHeader(request, token!)))
    );
  }

  /**
   * addAuthHeader
   * - If `request` is the refresh endpoint itself, callers sometimes want a different header
   *   such as 'refresh-token' header. If your refresh endpoint needs that, modify accordingly.
   */
  private addAuthHeader(request: HttpRequest<any>, token: string) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`
      }
    });
  }
}
